import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PageHeader1 from "../../components/common/PageHeader1";
import axios from "axios";
import toast from "react-hot-toast";

function RevenueConsultationReport() {
  const [rowsData, setRowsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [paginationDetail, setPaginationDetail] = useState({
    totalRecords: 0,
  });

  // Filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [type, setType] = useState("chat");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [appliedType, setAppliedType] = useState("chat");

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Load data on mount and when pagination/filters change
  useEffect(() => {
    fetchRevenueConsultationData();
  }, [paginationModel, appliedStartDate, appliedEndDate, appliedType]);

  const fetchRevenueConsultationData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vedhvidhi-admin-token");
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;

      const params = {
        page,
        limit,
        type: appliedType || "chat"
      };

      // Add date filters if applied
      if (appliedStartDate) {
        params.startDate = appliedStartDate;
      }
      if (appliedEndDate) {
        params.endDate = appliedEndDate;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/revenueConsultationReport`,
        {
          params: params,
          headers: { Authorization: token },
        }
      );

      // Handle response
      let data = [];
      if (response.data?.success && Array.isArray(response.data.data)) {
        data = response.data.data;
        setPaginationDetail({
          totalRecords: response.data?.paginationDetail?.totalRecords || 0,
        });
      } else if (Array.isArray(response.data?.data)) {
        data = response.data.data;
        setPaginationDetail({
          totalRecords: response.data?.paginationDetail?.totalRecords || 0,
        });
      }
      setRowsData(data);
    } catch (error) {
      console.error("Error fetching revenue consultation report:", error);
      toast.error(error.response?.data?.message || "Failed to fetch revenue consultation report");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    // If one date is selected, both must be selected
    if ((startDate && !endDate) || (!startDate && endDate)) {
      toast.error("Please select both start date and end date, or leave both empty");
      return;
    }

    // Apply filters (can be type only, dates only, or both)
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedType(type);
    setPaginationModel({ page: 0, pageSize: 10 }); // Reset to first page when applying filter
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setType("chat");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setAppliedType("chat");
    setPaginationModel({ page: 0, pageSize: 10 });
    fetchRevenueConsultationData();
  };

  // const formatDateTime = (dateString) => {
  //   if (!dateString) return "N/A";
  //   return new Date(dateString).toLocaleString();
  // };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "0.00";
    return `${parseFloat(value).toFixed(2)}`;
  };

  // Export to CSV - Fetches all data from API with filters (no pagination)
  const handleExportSubmit = async () => {
    setIsExporting(true);

    try {
      const token = localStorage.getItem("vedhvidhi-admin-token");

      // Build params with filters but NO pagination
      const params = {
        type: appliedType || "chat"
      };

      // Add date filters if applied
      if (appliedStartDate) {
        params.startDate = appliedStartDate;
      }
      if (appliedEndDate) {
        params.endDate = appliedEndDate;
      }

      // Fetch all data from API (no pagination)
      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/revenueConsultationReport`,
        {
          params: params,
          headers: { Authorization: token },
        }
      );

      // Handle response
      let exportData = [];
      if (response.data?.success && Array.isArray(response.data.data)) {
        exportData = response.data.data;
      } else if (Array.isArray(response.data?.data)) {
        exportData = response.data.data;
      }

      if (exportData.length === 0) {
        toast.error("No data available to export");
        setIsExporting(false);
        setExportDialogOpen(false);
        return;
      }

      const escapeCsvCell = (cell) => {
        if (cell === null || cell === undefined) return '';
        const cellString = String(cell);
        if (cellString.includes(',') || cellString.includes('"') || cellString.includes('\n')) {
          return `"${cellString.replace(/"/g, '""')}"`;
        }
        return cellString;
      };

      // Format data for export
      const exportableColumns = columns.filter(col => col.field !== 'actions');
      const headerRow = exportableColumns.map(col => escapeCsvCell(col.headerName)).join(',');

      const dataRows = exportData.map((item, index) =>
        exportableColumns.map(col => {
          let value = '';

          if (col.field === 'sono') {
            value = index + 1;
          } else if (col.field === 'userName') {
            value = item.userName || "N/A";
          } else if (col.field === 'astrologerName') {
            value = item.astrologerName || "N/A";
          } else if (col.field === 'totalCharge' || col.field === 'rateForBooking' ||
            col.field === 'adminShare' || col.field === 'astroShare' ||
            col.field === 'giftAmountDeduction' || col.field === 'actualAmountDeduction') {
            value = formatCurrency(item[col.field] || 0);
          } else if (col.field === 'createdAt' || col.field === 'updatedAt') {
            value = item[col.field] ? new Date(item[col.field]).toLocaleString() : "N/A";
          } else {
            value = item[col.field] || "";
          }

          return escapeCsvCell(value);
        }).join(',')
      );

      const csvContent = [headerRow, ...dataRows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      const fileName = (appliedStartDate && appliedEndDate)
        ? `revenue-consultation-report-${appliedStartDate}-to-${appliedEndDate}.csv`
        : `revenue-consultation-report-${appliedType || 'chat'}.csv`;
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Export successful! ${exportData.length} records exported.`);
      setExportDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || `Export failed: ${error.message}`);
      console.error("Export error:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    { field: "sono", headerName: "S.No.", width: 80 },
    { field: "userName", headerName: "User Name", width: 180 },
    { field: "astrologerName", headerName: "Astrologer Name", width: 200 },
    {
      field: "totalCharge",
      headerName: "Total Charge",
      width: 130,
      // valueFormatter: (params) => formatCurrency(params?.value)
    },
    {
      field: "rateForBooking",
      headerName: "Rate For Booking",
      width: 150,
      // valueFormatter: (params) => formatCurrency(params?.value)
    },
    {
      field: "adminShare",
      headerName: "Admin Share",
      width: 130,
      // valueFormatter: (params) => formatCurrency(params?.value)
    },
    {
      field: "astroShare",
      headerName: "Astro Share",
      width: 130,
      // valueFormatter: (params) => formatCurrency(params?.value)
    },
    {
      field: "giftAmountDeduction",
      headerName: "Gift Amount Deduction",
      width: 180,
      // valueFormatter: (params) => formatCurrency(params?.value)
    },
    {
      field: "actualAmountDeduction",
      headerName: "Actual Amount Deduction",
      width: 200,
      // valueFormatter: (params) => formatCurrency(params?.value)
    },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 180,
      // valueFormatter: (params) => formatDateTime(params?.value)
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 180,
      // valueFormatter: (params) => formatDateTime(params?.value)
    },
  ];

  const rowCount = paginationDetail?.totalRecords || 0;

  const rows = (rowsData || []).map((item, index) => {
    return {
      id: item._id || item.id || `row-${index}`,
      sono: paginationModel.page * paginationModel.pageSize + index + 1,
      userName: item.userName || "N/A",
      astrologerName: item.astrologerName || "N/A",
      totalCharge: item.totalCharge?.toFixed(2) || 0,
      rateForBooking: item.rateForBooking?.toFixed(2) || 0,
      adminShare: item.adminShare?.toFixed(2) || 0,
      astroShare: item.astroShare?.toFixed(2) || 0,
      giftAmountDeduction: item.giftAmountDeduction?.toFixed(2) || 0,
      actualAmountDeduction: item.actualAmountDeduction?.toFixed(2) || 0,
      createdAt: item.createdAt || "",
      updatedAt: item.updatedAt || "",
    };
  });

  return (
    <div className="body d-flex py-1">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Revenue Consultation Report" />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <Box sx={{ width: "100%" }}>
                  {/* Filter Section */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-end" gap={2} mb={2} flexWrap="wrap">
                    {/* Left side - Type filter */}
                    <Box display="flex" alignItems="flex-end" gap={2}>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={type}
                          label="Type"
                          onChange={(e) => setType(e.target.value)}
                        >
                          <MenuItem value="chat">Chat</MenuItem>
                          <MenuItem value="call">Call</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Right side - Date filters */}
                    <Box display="flex" alignItems="flex-end" gap={2} flexWrap="wrap">
                      <TextField
                        type="date"
                        label="Start Date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ width: '180px' }}
                      />
                      <TextField
                        type="date"
                        label="End Date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ width: '180px' }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApplyFilter}
                        disabled={loading}
                        sx={{ height: '40px' }}
                      >
                        {loading ? "Loading..." : "Apply"}
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => setExportDialogOpen(true)}
                        sx={{ height: '40px' }}
                      >
                        Export
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleClearFilters}
                        sx={{ height: '40px' }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Box>

                  <Box sx={{ height: 600 }}>
                    <DataGrid
                      columns={columns}
                      rows={rows}
                      rowCount={rowCount}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationMode="server"
                      disableSelectionOnClick
                      loading={loading}
                      getRowId={(row) => row.id}
                    />
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => !isExporting && setExportDialogOpen(false)}>
        <DialogTitle>Export Revenue Consultation Report</DialogTitle>
        <DialogContent>
          <Typography>
            This will export the currently displayed data (including any active filters) as a CSV file.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={isExporting}>Cancel</Button>
          <Button onClick={handleExportSubmit} variant="contained" disabled={isExporting}>
            {isExporting ? "Exporting..." : "Confirm & Export"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default RevenueConsultationReport;

